"use client";

import Image from "next/image";
import { memo, useState } from "react";
import { cn } from "@/lib/utils";

const HEADSHOT_ASPECT = 600 / 436;
const LOGO_SOURCE_SCALE = 1.5;

export function playerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return `${first}${last}`.toUpperCase();
}

type AvatarProps = { name: string; imageUrl: string | null; size: number; className?: string };

const Avatar = memo(function Avatar({ name, imageUrl, size, className, fit }: AvatarProps & { fit: "cover" | "contain" }) {
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  const showImage = imageUrl !== null && imageUrl !== brokenUrl;
  const cover = fit === "cover";
  const imageWidth = cover ? Math.ceil(size * HEADSHOT_ASPECT) : size;
  const sourceScale = cover ? 1 : LOGO_SOURCE_SCALE;
  return (
    <span
      aria-hidden="true"
      data-testid={cover ? "player-avatar" : "team-logo"}
      className={cn("flex flex-shrink-0 select-none items-center justify-center overflow-hidden", (cover || !showImage) && "rounded-full bg-white/[0.06] font-semibold text-zinc-400", className)}
      style={{ width: size, height: size, fontSize: Math.round(size * (cover ? 0.34 : 0.4)) }}
    >
      {showImage ? (
        <Image src={imageUrl} alt="" width={Math.ceil(imageWidth * sourceScale)} height={Math.ceil(size * sourceScale)} quality={75} draggable={false} onError={() => setBrokenUrl(imageUrl)} style={{ width: imageWidth, height: size }} className={cn("max-w-none flex-none", cover ? "object-cover object-top" : "object-contain")} />
      ) : (
        playerInitials(name)
      )}
    </span>
  );
});

export function PlayerAvatar(props: AvatarProps) {
  return <Avatar {...props} fit="cover" />;
}

export function TeamLogo(props: AvatarProps) {
  return <Avatar {...props} fit="contain" />;
}

type TeamRef = { name: string; imageUrl: string | null };

export function TeamLogoPair({ away, home, size, className }: { away: TeamRef; home: TeamRef; size: number; className?: string }) {
  const each = Math.round(size * 0.7);
  return (
    <span aria-hidden="true" data-testid="team-logo-pair" className={cn("relative flex-shrink-0 select-none", className)} style={{ width: size, height: size }}>
      <Avatar name={away.name} imageUrl={away.imageUrl} size={each} fit="contain" className="absolute left-0 top-0" />
      <Avatar name={home.name} imageUrl={home.imageUrl} size={each} fit="contain" className="absolute bottom-0 right-0" />
    </span>
  );
}
